import React from 'react';

/**
 * A single quiz question. Renders an input pre-submission and feedback
 * post-submission. Answer keys are never requested or shown before submit —
 * `result` only exists after the student submits.
 *
 * Props:
 *  - question: { id, order, prompt, question_type('mcq'|'short_answer'), options:[str] }
 *  - value: current student response string
 *  - onChange: (response) => void
 *  - result: optional { correct, awarded, feedback } from the attempt
 *  - disabled: bool (locks inputs after submission)
 */
const QuizQuestion = ({ question, value, onChange, result, disabled }) => {
  const isMcq = question.question_type === 'mcq';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
          <span className="text-gray-400 dark:text-gray-500 mr-2">Q{question.order}.</span>
          {question.prompt}
        </p>
        {result && (
          <span
            className={`flex-shrink-0 px-2 py-1 text-xs rounded-full ${
              result.correct
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}
          >
            {result.correct ? 'Correct' : 'Review'}
            {typeof result.awarded === 'number' && ` · ${result.awarded}`}
          </span>
        )}
      </div>

      <div className="mt-3">
        {isMcq ? (
          <div className="space-y-2">
            {(question.options || []).map((opt, idx) => (
              <label
                key={idx}
                className={`flex items-center p-2 rounded-md border cursor-pointer transition-colors ${
                  value === opt
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                } ${disabled ? 'cursor-default opacity-90' : ''}`}
              >
                <input
                  type="radio"
                  name={`q-${question.id}`}
                  value={opt}
                  checked={value === opt}
                  onChange={() => onChange(opt)}
                  disabled={disabled}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">{opt}</span>
              </label>
            ))}
          </div>
        ) : (
          <textarea
            rows={4}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder="Type your answer…"
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm disabled:opacity-70"
          />
        )}
      </div>

      {result && result.feedback && (
        <div className="mt-3 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 rounded-md p-3 border border-gray-200 dark:border-gray-700">
          {result.feedback}
        </div>
      )}
    </div>
  );
};

export default QuizQuestion;
